package dev.lingvo.shared

import kotlinx.serialization.Serializable

/** Where a proposal has got to. */
@Serializable
enum class ProposalState { PENDING, ACCEPTED, REJECTED, SUPERSEDED }

/**
 * One suggestion from an agent, waiting for a person.
 *
 * **A proposal is not a translation.** It is absent from exports, absent from progress, and
 * overwrites nothing. Somebody accepts it and only then does it become a translation — with their
 * name on the acceptance, which is the point: the agent proposed, a human decided.
 */
@Serializable
data class ProposalDto(
    val id: String,
    val keyId: String,
    val keyName: String,
    val locale: String,
    val sourceValue: String? = null,
    val value: String? = null,
    val pluralForms: Map<PluralCategory, String>? = null,
    /** What is stored today, so a reviewer sees what would be replaced rather than guessing. */
    val currentValue: String? = null,
    val agentName: String? = null,
    val runId: String? = null,
    /**
     * Why the agent suggested this, in its own words. At most 500 characters.
     *
     * Null on every proposal written before the column existed, and on every one whose author chose
     * not to say — which is a real answer, and not the same as an empty string.
     */
    val rationale: String? = null,
    /**
     * `high`, `medium` or `low`.
     *
     * **The cheapest triage a reviewer has.** Somebody deciding four thousand German strings needs
     * an order to read them in, and without this every proposal looks equally trustworthy. The agent
     * is the only party who can supply it: after the fact nobody can reconstruct what it was unsure
     * about.
     */
    val confidence: String? = null,
    val createdAt: String,
    /**
     * The English has changed since this was proposed.
     *
     * A proposal answering a question nobody is asking any more, and accepting it would import a
     * translation of text that no longer exists.
     */
    val stale: Boolean = false,
    /** The proposal's recorded source, distinct from today's [sourceValue]. */
    val sourceSnapshot: SourceSnapshotDto? = null,
)

@Serializable
data class ProposalPageDto(
    val proposals: List<ProposalDto> = emptyList(),
    val total: Int = 0,
)

/**
 * A suggestion, from a caller that is not the remote MCP server.
 *
 * Every proposal in the product was written by `McpRoutes` calling `ProposalRepo.propose`
 * in-process, so the CLI — and therefore the agent in the customer's own repository — could not
 * reach the proposal model at all. The key is named rather than identified: a caller that has just
 * read a brief has key *names*, and making it look up a UUID first is a round trip to learn
 * something it already knew.
 */
@Serializable
data class CreateProposalRequest(
    val key: String,
    val locale: String,
    val value: String? = null,
    val pluralForms: Map<PluralCategory, String>? = null,
    val rationale: String? = null,
    val confidence: String? = null,
    /**
     * Groups everything one pass produced, so a reviewer can accept it as a batch.
     *
     * Optional, and minted by the caller: `lingvo propose` in a loop over four thousand strings is
     * one review, not four thousand, and the run is the only thing that can say which proposals
     * belong together.
     */
    val runId: String? = null,
)

/** What a caller gets back, so it can name the proposal it just made. */
@Serializable
data class ProposalCreatedDto(val id: String, val keyName: String, val locale: String)

/** The confidence values this surface accepts, named once. */
val PROPOSAL_CONFIDENCE: List<String> = listOf("high", "medium", "low")

/** What a rationale may run to before it is refused rather than silently cut in half. */
const val MAX_RATIONALE_CHARS = 500

/** Accept or reject, one or a whole run. */
@Serializable
data class DecideProposalsRequest(
    val ids: List<String> = emptyList(),
    /** Decide everything a single agent run produced, which is the review people actually do. */
    val runId: String? = null,
    val accept: Boolean,
)

/** Bounded named results for callers explicitly requesting a decision receipt. */
const val MAX_PROPOSAL_DECISION_RESULTS = 100

@Serializable
enum class ProposalDecisionOutcome { ACCEPTED, REJECTED, SKIPPED }

@Serializable
data class ProposalDecisionResultDto(
    val proposalId: String,
    val outcome: ProposalDecisionOutcome,
    val keyId: String? = null,
    val locale: String? = null,
    /** The committed draft's revision at acceptance, never an approval or future-state assertion. */
    val targetRevision: String? = null,
)

@Serializable
data class ProposalDecisionReceiptDto(
    val decided: Int,
    /** Null distinguishes an old aggregate-only response from an identified empty result. */
    val results: List<ProposalDecisionResultDto>? = null,
    val resultsTruncated: Boolean = false,
    val projectSlug: String? = null,
    val runId: String? = null,
)
